import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/types";
import {
  FetchTranscriptsRequest,
  FetchTranscriptsResponse,
  TranscriptSummary,
} from "@/modules/transcripts/features/TranscriptManagement/api/fetchTranscripts/types";
import { fetchTranscripts } from "@/modules/transcripts/features/TranscriptManagement/api/fetchTranscripts/fetchTranscriptsApi";
import { DeleteTranscriptResponse } from "@/modules/transcripts/features/TranscriptManagement/api/deleteTranscript/types";
import { deleteTranscript } from "@/modules/transcripts/features/TranscriptManagement/api/deleteTranscript/deleteTranscriptApi";

export interface TranscriptItem extends TranscriptSummary {}

interface TranscriptListState {
  transcripts: TranscriptItem[];
  totalCount: number;

  fetchLoading: boolean;
  fetchError: string | null;

  deleteLoading: boolean;
  deleteError: string | null;
}

const initialState: TranscriptListState = {
  transcripts: [],
  totalCount: 0,

  fetchLoading: false,
  fetchError: null,

  deleteLoading: false,
  deleteError: null,
};

export const transcriptListSlice = createSlice({
  name: "transcriptList",
  initialState,
  reducers: {
    setFetchLoading(state, action: PayloadAction<boolean>) {
      state.fetchLoading = action.payload;
    },
    setFetchError(state, action: PayloadAction<string | null>) {
      state.fetchError = action.payload;
    },
    setTranscripts(state, action: PayloadAction<TranscriptItem[]>) {
      state.transcripts = action.payload;
    },
    setTotalCount(state, action: PayloadAction<number>) {
      state.totalCount = action.payload;
    },
    setDeleteLoading(state, action: PayloadAction<boolean>) {
      state.deleteLoading = action.payload;
    },
    setDeleteError(state, action: PayloadAction<string | null>) {
      state.deleteError = action.payload;
    },
    removeTranscript(state, action: PayloadAction<number>) {
      state.transcripts = state.transcripts.filter(
        (transcript) => transcript.id !== action.payload,
      );
      state.totalCount = Math.max(0, state.totalCount - 1);
    },
  },
});

export const {
  setFetchLoading,
  setFetchError,
  setTranscripts,
  setTotalCount,
  setDeleteLoading,
  setDeleteError,
  removeTranscript,
} = transcriptListSlice.actions;

export const fetchTranscriptsAction =
  (requestData: FetchTranscriptsRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setFetchLoading(true));
    dispatch(setFetchError(null));

    try {
      const response: FetchTranscriptsResponse =
        await fetchTranscripts(requestData);
      dispatch(setTranscripts(response.data));
      dispatch(setTotalCount(response.meta?.totalCount || 0));
    } catch (error) {
      dispatch(setFetchError((error as Error).message));
    } finally {
      dispatch(setFetchLoading(false));
    }
  };

export const deleteTranscriptAction =
  (id: number): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setDeleteLoading(true));
    dispatch(setDeleteError(null));

    try {
      const response: DeleteTranscriptResponse = await deleteTranscript({ id });
      if (response.success) {
        dispatch(removeTranscript(id));
      }
    } catch (error) {
      dispatch(setDeleteError((error as Error).message));
    } finally {
      dispatch(setDeleteLoading(false));
    }
  };

export default transcriptListSlice.reducer;